import React, { JSX, useMemo } from 'react';
import { Platform, View, ViewStyle, TextStyle, StyleSheet } from 'react-native';
import { Text, Title } from '@constants/Themed';
import Button from '../../../components/UI/Button/Button';
import styles, { webResponsivePadding, webResponsiveHeight, getModernCardStyle } from '@stylesheet';
import { XStack } from 'tamagui';
import { useDeviceType } from '@hooks/useDeviceType';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

interface RowItemTypes {
  title?: string;
  titleStyle?: TextStyle;
  icon?: string | JSX.Element;
  onPress?: () => void;
  subtitle?: string;
  iconOnly?: boolean;
  red?: boolean;
  caution?: boolean;
  buttonText?: string;
  style?: ViewStyle | ViewStyle[];
  customComponent?: JSX.Element;
  buttonColor?: string;
  forceRow?: boolean;
  forceColumn?: boolean;
  value?: string | number;
  componentMinWidth?: number | string;
  testID?: string;
}

// Utility to flatten style arrays for web
const flattenStyle = (style: ViewStyle | ViewStyle[]): ViewStyle =>
  Array.isArray(style) ? Object.assign({}, ...style) : style;

const RowComponent: React.FC<RowItemTypes> = ({
  value,
  forceRow,
  forceColumn,
  titleStyle,
  customComponent,
  title,
  subtitle,
  icon,
  iconOnly,
  red,
  componentMinWidth,
  buttonText,
  buttonColor,
  onPress,
  style,
  testID,
}) => {
  const { isTablet } = useDeviceType();
  const theme = themeSelector();

  const spacing = useMemo(
    () => ({
      xs: webResponsivePadding(4),
      sm: webResponsivePadding(8),
      md: webResponsivePadding(12),
      lg: webResponsivePadding(16),
      xl: webResponsivePadding(20),
    }),
    []
  );

  // Container style
  const containerStyle: ViewStyle[] = useMemo(() => {
    const base: ViewStyle = { alignSelf: 'stretch', flex: 1 };
    if (!style) return [base];
    if (Array.isArray(style)) return [base, ...style.filter(Boolean)];
    return [base, style];
  }, [style]);

  const handlePress = useMemo(() => {
    if (!onPress) return undefined;
    return () => {
      try {
        onPress();
      } catch (error) {
        if (__DEV__) console.error('RowComponent onPress error:', error);
      }
    };
  }, [onPress]);

  const buttonColorValue = useMemo(() => {
    if (red) return 'red';
    if (buttonColor) return buttonColor;
    return undefined;
  }, [red, buttonColor]);

  const isValidValue = useMemo(() => {
    if (value == null) return false;
    const numValue = Number(value);
    return !isNaN(numValue) && isFinite(numValue);
  }, [value]);

  const cardStyle = useMemo(() => getModernCardStyle(theme, 'medium'), [theme]);

  // Value container (for customComponent, value text, or button)
  const valueContainerStyle: ViewStyle = useMemo(() => ({
    ...styles.centerEverything,
    flexGrow: forceColumn ? 0 : 1,
    alignSelf: forceColumn ? 'stretch' : 'center',
    width: forceColumn ? '100%' : undefined,
    minWidth: componentMinWidth ?? (forceColumn ? '100%' : Platform.OS === 'web' ? '50%' : '100%'),
  }), [forceColumn, componentMinWidth]);

  const buttonStyle: ViewStyle = useMemo(() => ({
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: forceColumn ? 'stretch' : 'center',
    flexGrow: forceColumn ? 0 : 1,
    maxWidth: forceColumn ? '100%' : Platform.OS === 'web' ? '100%' : undefined,
    minWidth: forceColumn ? '100%' : Platform.OS === 'web' ? '50%' : '100%',
    top: isTablet && !forceColumn ? -spacing.md : undefined,
    minHeight: webResponsiveHeight(40),
    marginVertical: forceColumn ? 0 : spacing.xs,
    width:
      forceColumn
        ? '100%'
        : Platform.OS !== 'web'
          ? isTablet
            ? '50%'
            : '100%'
          : undefined,
  }), [forceColumn, isTablet, spacing.md]);

  return (
    <View style={Platform.OS === 'web' ? flattenStyle(containerStyle) : containerStyle} testID={testID}>
      <View style={Platform.OS === 'web' ? flattenStyle(cardStyle) : cardStyle}>
        {title && (
          <View style={{ marginBottom: spacing.xs }}>
            <Title
              style={Platform.OS === 'web'
                ? flattenStyle([{ fontSize: 18, fontWeight: '600', marginBottom: subtitle ? spacing.xs : 0 }, titleStyle || {}])
                : [{ fontSize: 18, fontWeight: '600', marginBottom: subtitle ? spacing.xs : 0 }, titleStyle || {}]
              }
            >
              {title}
            </Title>
          </View>
        )}

        <XStack
          columnGap={forceColumn ? 0 : spacing.md}
          rowGap={forceColumn ? spacing.md : 0}
          flexDirection={forceColumn ? 'column' : 'row'}
          flexWrap="wrap"
        >
          {subtitle && (
            <Text
              style={{
                minWidth: forceColumn ? '100%' : '50%',
                flexGrow: forceColumn ? 0 : 1,
                fontWeight: '400',
                fontSize: 14,
                marginBottom: forceColumn ? spacing.xs : 0,
                opacity: theme ? 0.7 : 0.8,
              }}
            >
              {subtitle}
            </Text>
          )}

          {customComponent ? (
            <View style={Platform.OS === 'web' ? flattenStyle(valueContainerStyle) : valueContainerStyle}>
              {customComponent}
            </View>
          ) : (
            <View style={Platform.OS === 'web' ? flattenStyle(valueContainerStyle) : valueContainerStyle}>
              {isValidValue ? (
                <Text
                  style={{
                    flexGrow: forceColumn ? 0 : 1,
                    fontWeight: '500',
                    fontSize: 16,
                    minWidth: forceColumn ? '100%' : '50%',
                    textAlign: forceColumn ? 'left' : 'center',
                  }}
                >
                  {String(value)}
                </Text>
              ) : (
                <Button
                  customStyle={Platform.OS === 'web' ? flattenStyle(buttonStyle) : buttonStyle}
                  buttonText={buttonText}
                  defaultColor={buttonColorValue}
                  onPress={handlePress}
                  icon={icon}
                  iconStyle={{ left: 0 }}
                  iconOnly={Platform.OS !== 'web' && iconOnly}
                />
              )}
            </View>
          )}
        </XStack>
      </View>
    </View>
  );
};

export default RowComponent;
